import { type FC, useCallback, useRef, useState } from 'react'
import { Code, ConnectError } from '@connectrpc/connect'
import type { ReviewFormConfigResult } from '@customer/api/reviewApi'
import { reviewClient } from '@customer/api/reviewApi'
import { ReviewForm } from '@customer/template/ReviewForm/ReviewForm'
import { ReviewNotFound } from '@customer/template/ReviewForm/ReviewNotFound'
import { ReviewResult } from '@customer/template/ReviewForm/ReviewResult'
import { useReviewForm } from '@customer/template/ReviewForm/useReviewForm'
import { useQuery } from '@tanstack/react-query'

interface ReviewPageProps {
  slug: string
  /** ?deal=<id> による任意の案件紐付け */
  dealId?: bigint
}

interface GeneratedResult {
  generatedText: string
  googlePlaceId: string
}

/**
 * 施主向け口コミ作成フローのオーケストレーター。
 * 1. マウント時に getReviewFormConfig(slug) を取得
 * 2. ローディング → スピナー / NotFound or エラー → ReviewNotFound
 * 3. 成功 → ReviewForm（アンケート）→ 生成成功 → ReviewResult
 */
export const ReviewPage: FC<ReviewPageProps> = ({ slug, dealId }) => {
  const form = useReviewForm()
  const captchaTokenRef = useRef('')
  const [result, setResult] = useState<GeneratedResult | null>(null)

  const { data, isLoading, error } = useQuery<ReviewFormConfigResult, ConnectError>({
    queryKey: ['reviewFormConfig', slug],
    queryFn: async () => await reviewClient.getReviewFormConfig({ companySlug: slug }),
    retry: false,
    enabled: !!slug,
  })

  const handleSubmit = useCallback(async () => {
    const res = await form.generate({
      companySlug: slug,
      dealId,
      captchaToken: captchaTokenRef.current,
    })
    if (res) {
      setResult({ generatedText: res.generatedText, googlePlaceId: res.googlePlaceId })
    }
  }, [form, slug, dealId])

  const handleRestart = useCallback(() => {
    form.reset()
    captchaTokenRef.current = ''
    setResult(null)
  }, [form])

  if (isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-muted">
        <p className="text-muted-foreground">読み込み中...</p>
      </div>
    )
  }

  // NotFound（無効 slug / review_enabled=false）や取得失敗は専用ページを出す。
  // NotFound 以外のエラーも、施主にはフォーム自体が使えないため同一表示にする。
  if (error || !data) {
    if (error && error.code !== Code.NotFound) {
      console.error('[ReviewPage] フォーム設定の取得に失敗しました', { code: error.code })
    }
    return <ReviewNotFound />
  }

  if (result) {
    return (
      <ReviewResult
        companyName={data.companyName}
        generatedText={result.generatedText}
        googlePlaceId={result.googlePlaceId}
        onRestart={handleRestart}
      />
    )
  }

  return (
    <ReviewForm
      companyName={data.companyName}
      config={data.config}
      form={form}
      onCaptchaToken={token => {
        captchaTokenRef.current = token
      }}
      onSubmit={handleSubmit}
    />
  )
}
